import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { Button, Icon, IconsName, Text } from '@/components/atoms';
import { cva, VariantProps } from 'class-variance-authority';
import { buttonStyles } from '@/components/atoms/Button/Button';
import { icon } from '@/components/atoms/Icon/Icon';
import { AnimatePresence, motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import APP_CONFIG from '@/config/app';

const { MAIN_BACKDROP_BLUR } = APP_CONFIG.STYLES;

const modalStyles = cva('div', {
  variants: {
    size: {
      sm: 'h-[280px]',
      md: 'h-[350px]',
      lg: 'h-[700px]',
      xl: 'h-[750px]',
    },
    intent: {
      primary:
        'relative w-[520px] mx-auto max-w-3xl border-primary/10 bg-primary bg-opacity-15 rounded-2xs',
      ghost: 'bg-background/70 w-screen',
    },
  },
  defaultVariants: {
    size: 'md',
    intent: 'primary',
  },
});

const bodyStyles = cva('relative py-6 flex-auto', {
  variants: {
    bodyPadding: {
      withPadding: 'px-10',
      withoutPadding: '',
    },
  },
  defaultVariants: {
    bodyPadding: 'withPadding',
  },
});

interface IProps {
  showModal: boolean;
  title?: string;
  titleText?: ReactNode;
  body: string | ReactNode;
  footerButtonText: string;
  handleClickFooterButton?: () => void;
  handleClickCloseButton?: () => void;
  handleClickBackButton?: () => void;
  footerButtonDisabled?: boolean;
  bodyPadding?: boolean;
  footerButtonStyles?: VariantProps<typeof buttonStyles>;
  footerIconStyles?: VariantProps<typeof icon>;
  footerIcon?: IconsName;
}

interface ModalProps extends IProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  intent?: 'primary' | 'ghost' | null | undefined;
}

export default function Modal({
  showModal = true,
  title,
  titleText,
  body,
  footerButtonText,
  handleClickFooterButton,
  handleClickCloseButton,
  size = 'md',
  handleClickBackButton,
  intent = 'primary',
  footerButtonDisabled = false,
  bodyPadding = true,
  footerButtonStyles,
  footerIconStyles,
  footerIcon,
}: ModalProps) {
  const portalRef = useRef<Element | null>(null);
  const [mounted, setMounted] = useState<boolean>(false);

  useEffect(() => {
    portalRef.current = document.querySelector<HTMLElement>('#portal-modal');
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!showModal) return;

    document.body.style.overflowY = 'hidden';

    return () => {
      document.body.style.overflowY = 'unset';
    };
  }, [showModal]);

  return mounted && portalRef.current ? (
    <>
      {portalRef &&
        createPortal(
          <AnimatePresence>
            {showModal && (
              <motion.div
                initial={{ opacity: 0.3 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: '0.15',
                }}
              >
                <>
                  <div
                    className={`fixed left-1/2 top-[50%] -translate-y-2/4 -translate-x-2/4 
                                    z-50 flex justify-center overflow-x-hidden overflow-y-auto
                                    outline-none bg-background/70 focus:outline-none
                                    backdrop-blur${MAIN_BACKDROP_BLUR}
                                `}
                  >
                    <div
                      className={modalStyles({
                        size,
                        intent,
                      })}
                    >
                      {/*content*/}
                      <div className="relative flex flex-col w-full h-full rounded-2xs outline-none focus:outline-none">
                        <div className="flex items-center justify-between px-10 pt-10">
                          {!!handleClickBackButton ? (
                            <div
                              className="flex cursor-pointer"
                              onClick={handleClickBackButton}
                            >
                              <Icon name="chevronLeftNoSpacing" />
                            </div>
                          ) : (
                            <div className="w-md"></div>
                          )}
                          {title && (
                            <div className="flex justify-start">
                              <Text
                                value={title}
                                color={'primary'}
                                intent="text"
                                size="h3"
                              />
                            </div>
                          )}
                          {titleText && (
                            <div className="flex justify-start">
                              {titleText}
                            </div>
                          )}
                          {!!handleClickCloseButton && (
                            <div
                              onClick={handleClickCloseButton}
                              className="py-0 transition-all opacity-100 cursor-pointer hover:opacity-40"
                            >
                              <Icon name="close" />
                            </div>
                          )}
                        </div>
                        {/*header*/}
                        {/*body*/}
                        <div
                          className={bodyStyles({
                            bodyPadding: bodyPadding
                              ? 'withPadding'
                              : 'withoutPadding',
                          })}
                        >
                          <Text
                            value={body as string}
                            color={'secondary'}
                            intent="paragraph"
                            size="base"
                          />
                        </div>
                        {/*footer*/}
                        {!!footerButtonText && handleClickFooterButton && (
                          <div className="flex justify-center w-full p-10">
                            <Button
                              intent={intent || 'primary'}
                              fullWidth={true}
                              onClick={handleClickFooterButton}
                              disabled={footerButtonDisabled}
                              icon={footerIcon}
                              iconStyles={footerIconStyles}
                              {...footerButtonStyles}
                            >
                              {footerButtonText}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div
                    onClick={handleClickCloseButton}
                    className="fixed inset-0 z-40 bg-black opacity-25 cursor-pointer"
                  />
                </>
              </motion.div>
            )}
          </AnimatePresence>,
          portalRef.current,
        )}
    </>
  ) : (
    <></>
  );
}
